'use client';

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { clientFetchJsonDedupe } from '@/lib/client-fetch-dedupe';

export interface Country {
  id: number;
  code: string;
  code3?: string;
  name: string;
  name_translations?: Record<string, string>;
  phone_code?: string;
  currency_code?: string;
}

export interface CountriesContextValue {
  countries: Country[];
}

const CountriesContext = createContext<CountriesContextValue | undefined>(undefined);

export interface CountriesProviderProps {
  children: React.ReactNode;
  initialCountries: Country[];
}

/**
 * Layout'ta bir kez alınan ülke listesini sağlar; client'ta tekrarlayan /api/countries istekleri atılmaz.
 */
export function CountriesProvider({ children, initialCountries }: CountriesProviderProps) {
  const [countries, setCountries] = useState<Country[]>(initialCountries ?? []);

  useEffect(() => {
    setCountries(initialCountries ?? []);
  }, [initialCountries]);

  useEffect(() => {
    if ((initialCountries?.length ?? 0) > 0 || countries.length > 0) return;
    let alive = true;
    clientFetchJsonDedupe<{ data?: Country[] }>('countries:list', '/api/countries')
      .then((json) => {
        if (!alive) return;
        setCountries(Array.isArray(json?.data) ? json.data : []);
      })
      .catch(() => {
        if (alive) setCountries([]);
      });
    return () => {
      alive = false;
    };
  }, [initialCountries?.length, countries.length]);

  const value = useMemo<CountriesContextValue>(() => ({ countries }), [countries]);

  return (
    <CountriesContext.Provider value={value}>
      {children}
    </CountriesContext.Provider>
  );
}

export function useCountries(): Country[] {
  const ctx = useContext(CountriesContext);
  return ctx?.countries ?? [];
}

export function useCountriesContext(): CountriesContextValue {
  const ctx = useContext(CountriesContext);
  if (ctx === undefined) {
    return { countries: [] };
  }
  return ctx;
}
